import { supabase, ensureFreshAccessToken } from '../../shared/supabase/supabaseClient';
import { canRunOperationalBackgroundWork } from '../../shell/session/accountSetupService';
import { addSentryBreadcrumb } from '../../shared/sentry/sentryUser';
import { captureOpsFailure } from '../../shared/sentry/sentryOps';
import type { SyncDispatcherResponse } from './syncTypes';
import { recordStartupNetworkCall } from '../../shared/dev/networkCallAudit';

type SaleFiscalContext = {
  saleId: string;
  branchId?: string;
  fiscalMode?: string;
  total: number;
  items?: Array<{ productId: string; quantity: number; price: number }>;
  payments?: Array<{ methodId?: string }>;
};

const invokeEdge = async (path: string, body: Record<string, unknown>): Promise<void> => {
  const token = await ensureFreshAccessToken();
  if (!token) return;

  const { error } = await supabase.functions.invoke(path, { body });
  if (error) {
    throw new Error(`${path} failed: ${error.message}`);
  }
};

/** Post-void: kick de nota de crédito. Owner = `pos.fiscal_jobs` + worker; esto solo acelera. */
export const invokeArcaCreditNoteForVoidedSale = async (saleId: string): Promise<void> => {
  if (!saleId) return;
  await invokeEdge('arca-credit-note', { saleId });
};

/** Reintenta NC pendientes tras anulación fiscal. */
export const retryPendingCreditNotesViaClient = async (): Promise<number> => {
  if (!canRunOperationalBackgroundWork()) return 0;

  const token = await ensureFreshAccessToken();
  if (!token) return 0;

  const { data: sales, error } = await supabase
    .schema('pos')
    .from('sales')
    .select('id, credit_note_status, status')
    .eq('status', 'voided')
    .in('credit_note_status', ['pending', 'pending_retry'])
    .order('voided_at', { ascending: true })
    .limit(10);

  if (error || !sales?.length) return 0;

  let retried = 0;
  for (const row of sales as Array<{ id: string }>) {
    try {
      await invokeEdge('arca-credit-note', { saleId: row.id });
      retried += 1;
    } catch (err) {
      console.warn('[fiscalSideEffects] NC retry', row.id, err);
    }
  }
  return retried;
};

/** Mercado Pago QR tras sync — fiscal y non-fiscal. */
export const invokeMercadoPagoQrAfterSaleSync = async (ctx: SaleFiscalContext): Promise<void> => {
  if (!ctx.saleId) return;
  const hasMpQr =
    Array.isArray(ctx.payments) && ctx.payments.some((p) => p?.methodId === 'mercadopago_qr');
  if (!hasMpQr) return;

  try {
    await invokeEdge('mercadopago-qr', { saleId: ctx.saleId, total: ctx.total });
  } catch (err) {
    console.warn('[fiscalSideEffects] mercadopago-qr', err);
  }
};

/** ARCA factura tras sync — solo ventas fiscales. */
export const invokeArcaInvoiceAfterSaleSync = async (ctx: SaleFiscalContext): Promise<void> => {
  if (!ctx.saleId || ctx.fiscalMode === 'non_fiscal') return;

  try {
    await invokeEdge('arca-invoice', {
      saleId: ctx.saleId,
      customer: ctx.branchId ?? null,
      items: ctx.items ?? [],
    });
    addSentryBreadcrumb('fiscal', `Factura emitida: ${ctx.saleId}`, 'info', {
      saleId: ctx.saleId,
      branchId: ctx.branchId,
    });
  } catch (err) {
    addSentryBreadcrumb('fiscal', `Error emisión factura: ${ctx.saleId}`, 'error', {
      saleId: ctx.saleId,
      branchId: ctx.branchId,
      error: err instanceof Error ? err.message : String(err),
    });
    captureOpsFailure({
      module: 'fiscal',
      op: 'fiscal.arca_invoice',
      error: err,
      itemId: ctx.saleId,
      extra: { saleId: ctx.saleId, branchId: ctx.branchId ?? null },
    });
    console.warn('[fiscalSideEffects] arca-invoice', err);
  }
};

/** Post-venta: side effects tras apply_sale_command (best-effort, no bloquean ACK). */
export const invokeFiscalSideEffectsForSale = async (ctx: SaleFiscalContext): Promise<void> => {
  await invokeArcaInvoiceAfterSaleSync(ctx);
  await invokeMercadoPagoQrAfterSaleSync(ctx);
};

/** Reintenta facturas pendientes de la org (coordinador tras flush RPC). */
export const retryPendingFiscalInvoicesViaRpc = async (): Promise<number> => {
  if (!canRunOperationalBackgroundWork()) return 0;

  recordStartupNetworkCall('fiscal', 'retry_pending_invoices_for_org');
  const { data, error } = await supabase.schema('pos').rpc('retry_pending_invoices_for_org', {
    p_limit: 10,
  });
  if (error) {
    if (error.message !== 'organization_not_resolved') {
      console.warn('[fiscalSideEffects] retry_pending_invoices_for_org', error.message);
    }
    return 0;
  }
  const eligible = typeof data === 'object' && data !== null ? Number((data as { eligible?: number }).eligible ?? 0) : 0;
  if (eligible <= 0) return 0;

  const token = await ensureFreshAccessToken();
  if (!token) return 0;

  const { data: invoices, error: invError } = await supabase
    .schema('pos')
    .from('invoices')
    .select('sale_id, status, next_retry_at')
    .in('status', ['pending', 'pending_retry'])
    .is('related_invoice_id', null)
    .order('created_at', { ascending: true })
    .limit(10);

  if (invError || !invoices?.length) return 0;

  let retried = 0;
  for (const inv of invoices as Array<{ sale_id: string; next_retry_at: string | null }>) {
    if (inv.next_retry_at && new Date(inv.next_retry_at).getTime() > Date.now()) continue;
    try {
      await invokeEdge('arca-invoice', { saleId: inv.sale_id });
      retried += 1;
    } catch (err) {
      console.warn('[fiscalSideEffects] ARCA retry', inv.sale_id, err);
    }
  }
  return retried;
};

/**
 * Cooldown del barrido de reintentos fiscales. syncNow lo invoca en cada
 * reconnect/focus/timer; el server ya paces con next_retry_at, así que un
 * barrido cada 5 min alcanza. `force` = sync manual del usuario.
 */
const FISCAL_RETRY_COOLDOWN_MS = 5 * 60 * 1000;
let lastFiscalSweepAt = 0;

/** Expuesto para tests. */
export function resetFiscalSweepCooldownForTests(): void {
  lastFiscalSweepAt = 0;
}

export const invokeFiscalSideEffectsAfterFlush = async (
  opts?: { force?: boolean },
): Promise<SyncDispatcherResponse> => {
  if (!canRunOperationalBackgroundWork()) {
    return { processed: 0, retriedFiscalInvoices: 0, retriedCreditNotes: 0 };
  }
  const now = Date.now();
  if (!opts?.force && lastFiscalSweepAt > 0 && now - lastFiscalSweepAt < FISCAL_RETRY_COOLDOWN_MS) {
    return { processed: 0, retriedFiscalInvoices: 0, retriedCreditNotes: 0 };
  }
  lastFiscalSweepAt = now;
  const retriedFiscalInvoices = await retryPendingFiscalInvoicesViaRpc();
  const retriedCreditNotes = await retryPendingCreditNotesViaClient();
  return { processed: 0, retriedFiscalInvoices, retriedCreditNotes };
};
